import { Divider, makeStyles } from '@material-ui/core';
import { Warning } from '@material-ui/icons';
import clsx from 'clsx';
import React from 'react';
import { Trash } from 'react-feather';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { fileFilter, fileToDataUrl, openFileDialog, uploadPicture } from '../../../util/file';
import { Button, IconButton, Property, StatusCard } from '../../global';
import uploadCloud from './AwaitingReportUploadCloud.svg';

const useAwaitingReportClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        '& > hr': {
            background: theme.colors.neutral[3],
            marginBottom: 20,
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        height: 0,
        overflowY: 'auto',
        gap: 20,
    },
    feedback: {
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        color: theme.colors.neutral[1],
        background: theme.colors.amber[4],
        '& > div': {
            display: 'flex',
            alignItems: 'center',
            '& > svg': {
                color: theme.colors.amber[1],
                marginRight: 10,
            },
            '& > span': {
                ...theme.typography.content.label.strong,
            },
        },
        '& > span': {
            ...theme.typography.content.label.default,
            whiteSpace: 'pre-wrap',
            margin: '10px 0 0 35px',
        },
    },
    uploader: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 30,
        borderRadius: 5,
        border: '1px dashed currentColor',
        color: theme.colors.darkBlue[5],
        background: theme.colors.neutral[4],
        transition: `opacity ${theme.transitions.default}s`,
        '& > span:first-of-type': {
            ...theme.typography.headings.h2,
            marginTop: 10,
        },
        '& > span:last-of-type': {
            ...theme.typography.content.label.default,
            '& > span': {
                color: theme.colors.green[4],
                cursor: 'pointer',
            },
        },
        '&.uploaded': {
            opacity: 0.3,
        },
    },
    document: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px 20px',
        '& > span': {
            color: theme.colors.neutral[1],
            ...theme.typography.headings.h4,
            marginBottom: 0,
        },
        '& > button': {
            color: theme.colors.neutral[3],
        },
    },
    button: {
        alignSelf: 'flex-end',
    },
});

/**
 * Editor for projects in the awaiting report mode.
 */
export const AwaitingReport = () => {
    const classes = useAwaitingReportClasses();
    const { t, Trans } = usePrefixTranslation('project', { project: 'editor.AwaitingReport' });
    const dispatch = useTypedDispatch();
    const { project } = useTypedSelector(state => ({ project: state.project.changed }));
    const cls = { uploaded: (project?.report ?? '') != '' };

    const setReport = async (files: File[]) => {
        const filteredFiles = fileFilter(files, /.*/);
        const report = filteredFiles[0];
        if (!report) return;
        const name = await uploadPicture(await fileToDataUrl(report));
        dispatch(actions.project.edit({ report: name }));
    };

    if (!project) return <span>{'#project not in store'}</span>;

    return (
        <div className={classes.root}>
            <div className={classes.content}>
                {project.comment !== '' && (
                    <div className={classes.feedback}>
                        <div>
                            <Warning />
                            <span>{t('update-request', { date: project.awaiting_report_date })}</span>
                        </div>
                        <span>{project.comment}</span>
                    </div>
                )}
                <div
                    className={clsx(classes.uploader, cls)}
                    onDragOver={event => event.preventDefault()}
                    onDrop={event =>
                        !cls.uploaded && (event.preventDefault(), setReport([...event.dataTransfer.files]))
                    }
                >
                    <img src={uploadCloud} width={120} height={106} alt='upload' />
                    <span>{t('title')}</span>
                    <span>
                        <Trans
                            i18nKey={'subtitle'}
                            components={{
                                choose: (
                                    <span onClick={async () => !cls.uploaded && setReport(await openFileDialog('*'))} />
                                ),
                            }}
                        />
                    </span>
                </div>
                {project.report !== '' && (
                    <StatusCard className={classes.document} type='light' large>
                        <span>{project.report}</span>
                        <IconButton size='small' onClick={() => dispatch(actions.project.edit({ report: '' }))}>
                            <Trash />
                        </IconButton>
                    </StatusCard>
                )}
                <Property
                    name={t('input-comment')}
                    value={project.findings}
                    multiline
                    rows={6}
                    editable
                    onChange={(name, value) => dispatch(actions.project.edit({ findings: value ?? '' }))}
                />
            </div>
            <Divider />
            <Button
                className={classes.button}
                variant='contained'
                disabled={project.report === ''}
                onClick={() => {
                    dispatch(actions.project.edit({ status: 'under review' }));
                    dispatch(actions.project.commit());
                    dispatch(actions.project.update(true)).catch(error => {
                        dispatch(actions.project.edit({ status: 'awaiting report' }));
                        dispatch(actions.project.commit());
                    });
                }}
            >
                {t('button-submit')}
            </Button>
        </div>
    );
};
